import { useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, Shield, ShieldCheck, Swords, VolumeX } from "lucide-react";
import { buffTotals, misunderstanding } from "../../engine";
import {
  loadActivePlayerAvatar,
  loadActivePlayerName,
  PLAYER_AVATAR_EVENT,
  PLAYER_NAME_EVENT,
} from "../../lib/leaderboard";
import { PlayerPresentation } from "../../lib/playerPresentation";
import { useGame } from "../../state/GameProvider";

function Combatant({
  role,
  glyph,
  avatar,
  name,
  bar,
  caption,
  danger,
  impactKey,
}: {
  role: "player" | "enemy";
  glyph: string;
  avatar?: string;
  name: string;
  bar: number;
  caption: string;
  danger?: boolean;
  impactKey: number | null;
}) {
  const enemy = role === "enemy";
  return (
    <div className={`combatant ${role}`}>
      <div className="portrait-wrap">
        <motion.div
          className="portrait"
          aria-hidden
          animate={impactKey != null ? { x: [0, -3, 4, -2, 0] } : { x: 0 }}
          transition={{ duration: 0.32 }}
        >
          {avatar ? (
            <img
              className="portrait-img"
              src={avatar}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            glyph
          )}
        </motion.div>
        <AnimatePresence>
          {impactKey != null && (
            <motion.span
              key={impactKey}
              className={`portrait-impact ${enemy ? "hit" : "hurt"}`}
              initial={{ opacity: 0.85, scale: 0.55 }}
              animate={{ opacity: 0, scale: 1.8 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          )}
        </AnimatePresence>
      </div>
      <div className="cb-body">
        <span className="cb-name">
          {enemy ? <Swords size={13} /> : <Shield size={13} />}
          {name}
        </span>
        <div className={`metabar ${role}${danger ? " danger" : ""}`}>
          <i style={{ width: `${Math.max(0, Math.min(100, bar))}%` }} />
        </div>
        <small>{caption}</small>
      </div>
    </div>
  );
}

export function DuelHud() {
  const { state } = useGame();
  const presentation = useContext(PlayerPresentation);
  const { duel, fx } = state;
  const [playerName, setPlayerName] = useState(() => presentation ? "學子" : loadActivePlayerName());
  const [playerAvatar, setPlayerAvatar] = useState(() => presentation ? "" : loadActivePlayerAvatar());
  const resist = misunderstanding(duel);
  const comboPct = Math.round(Math.max(0, duel.combo - 1) * 20);
  const buffs = buffTotals(duel);

  const enemyImpact = fx && (fx.kind === "hit" || fx.kind === "combo" || fx.kind === "win") ? fx.id : null;
  const playerImpact = fx && (fx.kind === "miss" || fx.kind === "lose") ? fx.id : null;

  useEffect(() => {
    if (presentation) return;
    function update(event: Event) {
      setPlayerName(typeof (event as CustomEvent<string>).detail === "string" ? (event as CustomEvent<string>).detail : loadActivePlayerName());
    }
    window.addEventListener(PLAYER_NAME_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(PLAYER_NAME_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);

  useEffect(() => {
    if (presentation) return;
    function update(event: Event) {
      setPlayerAvatar(
        typeof (event as CustomEvent<string>).detail === "string"
          ? (event as CustomEvent<string>).detail
          : loadActivePlayerAvatar(),
      );
    }
    window.addEventListener(PLAYER_AVATAR_EVENT, update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener(PLAYER_AVATAR_EVENT, update);
      window.removeEventListener("storage", update);
    };
  }, []);

  return (
    <section className="duelhud" aria-label="對戰狀態">
      <div className="duel-portraits">
        <Combatant
          role="player"
          glyph="學"
          avatar={presentation?.avatar ?? playerAvatar}
          name={presentation?.name ?? playerName}
          bar={duel.playerHp}
          caption={`氣勢 ${duel.playerHp}/100 · 最高連擊 ${duel.bestCombo}`}
          danger={duel.playerHp <= 30}
          impactKey={playerImpact}
        />

        <div className="roundseal">
          <span className="rs-top">第</span>
          <strong>{duel.round}</strong>
          <span className="rs-bot">回合</span>
        </div>

        <Combatant
          role="enemy"
          glyph="曲"
          name="曲解者"
          bar={resist}
          caption={`誤解度 ${resist}%`}
          impactKey={enemyImpact}
        />
      </div>

      <div className="buffstrip" aria-label="本回合效果">
        {duel.combo >= 2 && (
          <span className="buffchip combo" title={`連對 ×${duel.combo}`}>
            <Flame size={12} />
            <span>{`連對 ×${duel.combo} · 傷害 +${comboPct}%`}</span>
          </span>
        )}
        {buffs.map((buff) => (
          <span
            key={buff.id}
            className={`buffchip buff-${buff.gate}${buff.stacks >= buff.requirement ? " met" : ""}`}
            title={`${buff.label} ${buff.stacks}/${buff.requirement}`}
          >
            {buff.label}
            <i key={buff.stacks}>
              {`${buff.stacks}/${buff.requirement}`}
            </i>
          </span>
        ))}
        {duel.shield > 0 && (
          <span className="buffchip status shield">
            <ShieldCheck size={12} />
            <span>{`護盾 ${duel.shield}`}</span>
          </span>
        )}
        {duel.silenced > 0 && (
          <span className="buffchip status silence">
            <VolumeX size={12} />
            <span>{`沉默 ${duel.silenced}`}</span>
          </span>
        )}
      </div>
    </section>
  );
}
